/* BulkResultModal — per-record outcome reporter for bulk operations.
 * Renders the canonical {requested, succeeded, failed, errors} shape
 * returned by every shim /bulk/* endpoint.
 */
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';

export function BulkResultModal({ title, summary, idField = 'id', onClose }) {
  const { requested = 0, succeeded = 0, failed = 0, errors = [] } = summary || {};

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-2xl" data-testid="emp-bulk-result">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>
            Per-record outcome for the batch operation.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-3 gap-2" data-testid="emp-bulk-counters">
          <div className="rounded border bg-ndor-surface-alt p-3 text-center dark:bg-zinc-900">
            <p className="text-xs text-slate-300">requested</p>
            <p className="text-2xl font-semibold" data-testid="emp-bulk-requested">{requested}</p>
          </div>
          <div className="rounded border border-green-500/40 bg-green-50 p-3 text-center dark:bg-green-950/20">
            <p className="text-xs text-green-700 dark:text-green-300">succeeded</p>
            <p className="text-2xl font-semibold text-green-700 dark:text-green-300"
              data-testid="emp-bulk-succeeded">{succeeded}</p>
          </div>
          <div className="rounded border border-red-500/40 bg-red-50 p-3 text-center dark:bg-red-950/20">
            <p className="text-xs text-rose-200 dark:text-red-300">failed</p>
            <p className="text-2xl font-semibold text-rose-200 dark:text-red-300"
              data-testid="emp-bulk-failed">{failed}</p>
          </div>
        </div>

        {errors.length > 0 && (
          <div className="mt-4 max-h-72 overflow-y-auto" data-testid="emp-bulk-errors">
            <p className="mb-2 text-sm font-medium">Per-record errors</p>
            <ul className="space-y-2">
              {errors.map((err, i) => (
                <li key={i}
                  className="flex flex-col gap-1 rounded border bg-ndor-surface-alt p-2 text-xs dark:bg-zinc-900"
                  data-testid={`emp-bulk-error-${i}`}>
                  <div className="flex items-center gap-2">
                    <Badge variant="destructive">{err.sqlstate || 'ERR'}</Badge>
                    <span className="font-mono text-slate-300">
                      {(err[idField] || JSON.stringify(err.item || ''))?.toString().slice(0, 40)}
                    </span>
                  </div>
                  <p className="text-slate-200 dark:text-zinc-300">
                    {err.message || JSON.stringify(err)}
                  </p>
                </li>
              ))}
            </ul>
          </div>
        )}

        {errors.length === 0 && (
          <p className="mt-4 text-sm text-slate-300" data-testid="emp-bulk-no-errors">
            All requested items processed without error.
          </p>
        )}

        <DialogFooter>
          <Button onClick={onClose} data-testid="emp-bulk-close">Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
